import React, { Component } from "react";
import { Cauldron } from "./Art";

// The witch reads your mind. Six court cards; you remember one. She stirs the
// cauldron and shows the cards again, and yours has gone. It works on nearly
// everyone, because every card is swapped, and people watching for their own
// card never look properly at the other five.

interface PlayingCard {
  rank: "K" | "Q" | "J";
  suit: "♠" | "♥" | "♦" | "♣";
}

const FIRST: PlayingCard[] = [
  { rank: "K", suit: "♠" },
  { rank: "Q", suit: "♥" },
  { rank: "J", suit: "♣" },
  { rank: "K", suit: "♦" },
  { rank: "Q", suit: "♣" },
  { rank: "J", suit: "♥" },
];

// Same ranks, other suits, and one card fewer: none of these was shown before.
const SECOND: PlayingCard[] = [
  { rank: "K", suit: "♣" },
  { rank: "Q", suit: "♦" },
  { rank: "J", suit: "♠" },
  { rank: "K", suit: "♥" },
  { rank: "Q", suit: "♠" },
];

const RED = new Set(["♥", "♦"]);
const NAMES = { K: "King", Q: "Queen", J: "Jack" };
const SUITS = { "♠": "spades", "♥": "hearts", "♦": "diamonds", "♣": "clubs" };
const STIR_MS = 2200;

// Kings wear crowns, queens witches' hats, and the jacks are jack-o'-lanterns.
const Motif = ({ rank, colour }: { rank: PlayingCard["rank"]; colour: string }) => {
  if (rank === "K") {
    return (
      <svg viewBox="0 0 32 24" aria-hidden="true">
        <path d="M3 20 1 5l8 7 7-10 7 10 8-7-2 15z" fill={colour} stroke="#2e2437" strokeWidth="1.4" strokeLinejoin="round" />
        <rect x="3" y="19" width="26" height="4" rx="1" fill="#f5b041" stroke="#2e2437" strokeWidth="1.2" />
      </svg>
    );
  }
  if (rank === "Q") {
    return (
      <svg viewBox="0 0 32 24" aria-hidden="true">
        <ellipse cx="16" cy="20" rx="14" ry="3" fill="#2e2437" />
        <path d="M8 20h16c-2-6-4-12-6-15-2-3-6-4-9-3 3 2 4 5 3 8z" fill="#2e2437" />
        <path d="M9.6 17.4h12.6" stroke={colour} strokeWidth="2" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 32 24" aria-hidden="true">
      <ellipse cx="16" cy="14" rx="12" ry="9" fill="#ff7a1a" stroke="#2e2437" strokeWidth="1.4" />
      <path d="M16 5c0-2 1-3.6 3-4" stroke="#4f7a2c" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M10 12l2.4-3 2.4 3zM17.2 12l2.4-3 2.4 3zM10 16c3 3 9 3 12 0l-2 .4-1 1.4-1.4-1.2-1.6 1.4-1.6-1.4-1.4 1.2-1-1.4z" fill="#2e2437" />
    </svg>
  );
};

const Card = ({ card }: { card: PlayingCard }) => {
  const colour = RED.has(card.suit) ? "#d9480f" : "#2e2437";
  return (
    <div className="hw-card" style={{ color: colour }} aria-label={`${NAMES[card.rank]} of ${SUITS[card.suit]}`} role="img">
      <span className="hw-card__corner">
        {card.rank}
        <br />
        {card.suit}
      </span>
      <span className="hw-card__motif">
        <Motif rank={card.rank} colour={colour} />
      </span>
      <span className="hw-card__suit">{card.suit}</span>
      <span className="hw-card__corner hw-card__corner--end">
        {card.rank}
        <br />
        {card.suit}
      </span>
    </div>
  );
};

type Step = "remember" | "stirring" | "vanished" | "reveal";

interface Props {
  onDone: () => void;
}

interface State {
  step: Step;
}

class CardTrick extends Component<Props, State> {
  state: State = { step: "remember" };
  private timer?: number;

  componentWillUnmount() {
    window.clearTimeout(this.timer);
  }

  stir = () => {
    this.setState({ step: "stirring" });
    this.timer = window.setTimeout(() => this.setState({ step: "vanished" }), STIR_MS);
  };

  render() {
    const { step } = this.state;

    if (step === "remember") {
      return (
        <div className="hw-trick">
          <p className="hw-eyebrow">Trick: the witch reads your mind</p>
          <h2>Pick a card. In your head.</h2>
          <p className="hw-lead">Don't click it, don't say it. Just stare at it and remember it.</p>
          <div className="hw-cards">
            {FIRST.map((card) => (
              <Card key={`${card.rank}${card.suit}`} card={card} />
            ))}
          </div>
          <button className="hw-btn hw-btn--orange" type="button" onClick={this.stir}>
            I've got one
          </button>
        </div>
      );
    }

    if (step === "stirring") {
      return (
        <div className="hw-trick hw-trick--stirring" aria-live="polite">
          <p className="hw-eyebrow">Hubble, bubble…</p>
          <h2>Think hard about your card.</h2>
          <svg className="hw-trick__cauldron" viewBox="0 0 140 110" aria-hidden="true">
            <Cauldron bubbling />
          </svg>
          <p className="hw-lead">The witch is plucking it from your mind.</p>
        </div>
      );
    }

    if (step === "vanished") {
      return (
        <div className="hw-trick" aria-live="polite">
          <p className="hw-eyebrow">Abracadabra</p>
          <h2>Your card has vanished.</h2>
          <p className="hw-lead">Five cards left. Look for yours.</p>
          <div className="hw-cards hw-cards--five">
            {SECOND.map((card) => (
              <Card key={`${card.rank}${card.suit}`} card={card} />
            ))}
          </div>
          <button className="hw-btn hw-btn--orange" type="button" onClick={() => this.setState({ step: "reveal" })}>
            It's gone! How?
          </button>
        </div>
      );
    }

    return (
      <div className="hw-trick" aria-live="polite">
        <p className="hw-eyebrow">The secret</p>
        <h2>Every card changed.</h2>
        <p className="hw-lead">
          You only watched yours, so you never noticed the other five were swapped too. Look again:
        </p>
        <div className="hw-compare">
          <span className="hw-compare__label">Before</span>
          <div className="hw-cards hw-cards--small">
            {FIRST.map((card) => (
              <Card key={`a${card.rank}${card.suit}`} card={card} />
            ))}
          </div>
          <span className="hw-compare__label">After</span>
          <div className="hw-cards hw-cards--small">
            {SECOND.map((card) => (
              <Card key={`b${card.rank}${card.suit}`} card={card} />
            ))}
          </div>
        </div>
        <p className="hw-science">
          <strong>Change blindness.</strong> Attention is a torch, not a floodlight: we see what we're looking for and
          miss almost everything else. Magicians live off it, and so do the best ads.
        </p>
        <button className="hw-btn hw-btn--orange" type="button" onClick={this.props.onDone}>
          Claim my treat
        </button>
      </div>
    );
  }
}

export default CardTrick;
